import { Clock } from 'lucide-react';
import { useMemo } from 'react';
import { useUiLabels } from '../labels.js';
import { Select, type SelectProps } from './Select.js';

export interface TimePickerProps extends Omit<
  SelectProps,
  'options' | 'value' | 'onChange' | 'icon' | 'placeholder'
> {
  /** `HH:MM`, 24-hour. */
  value: string;
  onChange: (value: string) => void;
  /** Minutes between options (default 15). A value off the grid is still shown and kept. */
  step?: number;
  /** Hides the clock icon in the button. */
  hideIcon?: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Themed time picker: Select's list of times every `step` minutes, labelled in the UI locale
 * (24-hour in Spanish and British English). Typing jumps to a time (e.g. "14" or "9:3").
 */
export function TimePicker({ value, onChange, step = 15, hideIcon, ...props }: TimePickerProps) {
  const { locale } = useUiLabels();
  const options = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
    const label = (time: string) => {
      const [h, m] = time.split(':').map(Number);
      return format.format(new Date(2024, 0, 1, h ?? 0, m ?? 0));
    };
    const times = new Set<string>();
    for (let minutes = 0; minutes < 24 * 60; minutes += step) {
      times.add(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
    }
    if (/^\d{2}:\d{2}$/.test(value)) times.add(value);
    return [...times].sort().map((time) => ({ value: time, label: label(time) }));
  }, [locale, step, value]);

  return (
    <Select
      {...props}
      value={value}
      onChange={onChange}
      options={options}
      icon={hideIcon ? undefined : <Clock className="text-subtle" />}
    />
  );
}
